import React, { CSSProperties, ReactNode } from "react";
import { DataTable, TableHeader, TableBody, TableRow, TableColumn, FontIcon } from "react-md";
import Button from "@material-ui/core/Button";
import { isBase64String, base64String } from "components/Homeowner/History/DataFormatters";

// Just wanted this to be a specific type in case we ever changed it, we wouldnt have to change it everywhere
export type MTableCellContents = ReactNode;

export interface ColSpanReturnType {
  children: MTableCellContents;
  className?: string,
};

type GenericWithExtraFields<T> = T & {
  depth?: number,
  ORIGINAL_INDEX?: number,
  body?: string | ReactNode, // some garbage TEAM note logic that should not exist in a generic component...
};

export interface ColumnDefinition<T = any> {
  /** The column name to show in the grid header */
  name: string,
  /** Optional styles only applied to the header */
  style?: CSSProperties,
  /** Optional styles on the table cell */
  cellStyle?: CSSProperties,
  /** Optional classname for a column */
  className?: string, 
  /** Provides data for the grid (either a string of a property name from the dataSource prop or a function that will be invoked with the entire row passed in from dataSource) */
  dataSource: (keyof T) | ((row: T) => MTableCellContents), 
  /** Optional width of the column */
  width?: number,
  /** Optional, if using depthProperty prop this will render whitespace before the text with a width based on how deep this row is */
  addDepthWhitespace?: boolean, 
  /** Optional string to render with html title prop */
  tooltip?: string,
  /** Applys proper styles to make the text overflow be truncated with "..." */
  overflowEllipsis?: boolean,
}

interface Props<T = any> {
  className?: string, // class name to put on the table if provided
  rowClassName?: string, // class name to put on each row
  /*
  {
    name, the column name to show in the grid header
    className, optional classname for a column
    dataSource, provides data for the grid (either a string of a property name to pull off of the dataSource prop or a function that will be invoked with the entire row passed in from dataSource)
    width, optional width of the column,
    addDepthWhitespace, optional boolean, if using depthProperty prop this will render whitespace before the text with a width based on how deep this row is
    tooltip, string to render with html title prop
  }
  */
  columnDefinitions: ColumnDefinition<T>[],
  /*
    property of each row to use as the key on the table row
    probably needed if you plan on editing the rows in the grid after it has mounted because otherwise it is keyed off of index which can cause rendering issues
  */
  keyProperty?: string,
  /*
    NOTE: every row must be keyed if using depth
    if depth property is supplied then MTable will render collapsible sections
    example of depth (with + to denote which rows will be collapsible and the depth on the right):
    +------------ (0)
      ----------- (1)
    + ----------- (1)
       ---------- (2)
      ----------- (1)
     ------------ (0)
     ------------ (0)
    +------------ (0)
    + ----------- (1)
       ---------- (2)
    MTable will handle figuring out which rows have children and which dont based on the depth
  */
  depthProperty?: string,
  data: GenericWithExtraFields<T>[], // an array of data to show in the table. the way it shows up is defined by the column definitions
  emptyTableText?: React.ReactNode | string,
  renderEmptyTable?: boolean, // pass true to render an empty table
  getColSpanData?: (row: T) => ColSpanReturnType, // return an object here to make the row span the whole table width
  onCellMouseOver?: (cellContents: MTableCellContents, row: T, colDef: ColumnDefinition) => void,
  onCellMouseOut?: (cellContents: MTableCellContents, row: T, colDef: ColumnDefinition) => void,
}

interface State {
  collapsedRowKeys: string[],
  hiddenRowKeys: string[],
}

export default class MTable<T = any> extends React.Component<Props<T>, State> {
  static defaultProps = {
    rows: [],
  };

  constructor(props: Props) {
    super(props);
    this.getKey = this.getKey.bind(this);
    this.getValue = this.getValue.bind(this);
    this.toggleRow = this.toggleRow.bind(this);
    this.getExpandCollapse = this.getExpandCollapse.bind(this);
    this.renderTableRowContent = this.renderTableRowContent.bind(this);

    this.state = {
      collapsedRowKeys: [],
      hiddenRowKeys: [],
    };
  }

  getKey(i: number, d: object) {
    if (this.props.keyProperty) return this.getValue(d, this.props.keyProperty);
    else return `row-${i}`;
  }

  getValue(data: object, property: string) {
    return property.split(".").reduce((acc, val) => acc[val], data);
  }

  toggleRow(key) {
    let collapsedRowKeys;
    let hiddenRowKeys = [];
    if (this.state.collapsedRowKeys.indexOf(key) >= 0) collapsedRowKeys = this.state.collapsedRowKeys.filter(k => k != key);
    else collapsedRowKeys = [...this.state.collapsedRowKeys, key];

    let collapseDepth = -1;

    this.props.data.forEach(d => {
      const depth = this.getValue(d, this.props.depthProperty);
      const key = this.getValue(d, this.props.keyProperty);
      if (depth > collapseDepth && collapseDepth > -1) hiddenRowKeys.push(key);
      else {
        if (collapsedRowKeys.indexOf(key) >= 0) collapseDepth = depth;
        else collapseDepth = -1;
      }
    });

    this.setState({ collapsedRowKeys, hiddenRowKeys });
  }

  getExpandCollapse(d: Record<string, any>) {
    if (this.props.data[d.ORIGINAL_INDEX + 1]?.depth > d.depth) {
      if (this.state.collapsedRowKeys.indexOf(d.key) > -1) {
        // don't need index for getKey here because a key needs to be provided if we are using toggleable rows
        return (<FontIcon secondary inherit className="icon expand-collapse" onClick={()=>this.toggleRow(this.getKey(null, d))}>
          keyboard_arrow_right
        </FontIcon>);
      } else {
        // don't need index for getKey here because a key needs to be provided if we are using toggleable rows
        return (<FontIcon secondary inherit className="icon expand-collapse" onClick={()=>this.toggleRow(this.getKey(null, d))}>
          keyboard_arrow_down
        </FontIcon>);
      }
    } else {
      return null;
    }
  }

  renderTableRowContent(row: GenericWithExtraFields<T>, i: number) {
    const { columnDefinitions, depthProperty, getColSpanData, rowClassName, onCellMouseOver, onCellMouseOut } = this.props;

    let colSpanData = getColSpanData?.(row);

    let modifiedRowClassName = rowClassName;
    let inner: ReactNode = null;
    if (colSpanData) {
      modifiedRowClassName += ` ${colSpanData.className}`;
      inner = <TableColumn key={0} colSpan={columnDefinitions.length}>{colSpanData.children}</TableColumn>;
    } else {
      inner = columnDefinitions.map((colDef, i) => {
        const { dataSource, className, addDepthWhitespace, width, cellStyle } = colDef;
        const expandCollapseButton = this.getExpandCollapse(row);
        // 20 * : pad 20 pixels for each amount of depth
        // - !!expandCollapseButton : pad one less amount of 20 if theres an expand collapse button (yes im doing math with a boolean AWWW YEA)
        // + 1 : start with 20px padding because rows that have 0 depth and have an expand collapse button would otherwise end up with negative padding
        let style: CSSProperties = (addDepthWhitespace && depthProperty) ? { paddingLeft: `${20 * (this.getValue(row, depthProperty) - (!!expandCollapseButton ? 1 : 0) + 1)}px` } : null;
        if (width) style = style ? {...style, width: width} : {width: width};
        if (cellStyle) style = { ...style, ...cellStyle };

        if (typeof row.body === "string" ) {
          let isImageAndString = row.body.split("\n").filter(item => item !== "");
          const imageStringList = Array.isArray(isImageAndString) && isImageAndString.some(item => item.includes(base64String)) ? isImageAndString.map(item => isBase64String(item)).filter(item => item !== undefined) : null;
          if (imageStringList?.length) { // renders note with text and screenshot(s)
            row.body = <div className="TEAM-note-container">{imageStringList.map((item, idx) => item ? <img src={isImageAndString[idx]} alt="TEAM-note-screenshot" className="TEAM-note-screenshot" /> : isImageAndString[idx])}</div>;
          } 
          if (!imageStringList && ((row.body as string).includes(base64String))) { // renders notes with just screenshots
            row.body = <img src={(row.body as string)} alt="TEAM-note-screenshot" className="TEAM-note-screenshot" />;
          }
        }
        
        const cellContents = typeof dataSource === "function" ? dataSource(row) : this.getValue(row, dataSource as string);

        let mouseEvents: any = {};
        if (onCellMouseOver) mouseEvents.onMouseOver = () => onCellMouseOver(cellContents, row, colDef);
        if (onCellMouseOut) mouseEvents.onMouseOut = () => onCellMouseOut(cellContents, row, colDef);

        return (
          <TableColumn key={i} className={className} style={style} {...mouseEvents}>
            {i === 0 && depthProperty && expandCollapseButton}
            {cellContents}
          </TableColumn>
        ); 
      });
    }

    return (
      <TableRow className={modifiedRowClassName} key={(this.getKey(i, row) || i)}>
        {inner}
      </TableRow>
    );
  }

  render() {
    const { columnDefinitions, data, emptyTableText, renderEmptyTable, className, rowClassName, depthProperty, keyProperty } = this.props;
    return (
      (data && data.length || renderEmptyTable) ? (
        <DataTable plain className={"m-table " + (className || "")}>
          <TableHeader>
            <TableRow className="m-table-header-row">
              {columnDefinitions.map(({ name, style, tooltip }, index) =>
                // @ts-ignore apparently TableColumn doesnt have a title prop, lets pretend it does
                <TableColumn title={tooltip as unknown} className="m-table-header-cell" style={style} key={`${name}-${index}`}>
                  {name}
                </TableColumn>)}
            </TableRow>
          </TableHeader>
          {(data && data.length) && <TableBody>
            {data.map((d, i) => { d.ORIGINAL_INDEX = i; return d; })
                 .filter(d => !depthProperty || this.state.hiddenRowKeys.indexOf(this.getValue(d, keyProperty)) == -1)
                 .map((d, i) => this.renderTableRowContent(d, i))}
          </TableBody>}
        </DataTable>
      ) : (
          <Button
            fullWidth
            disabled
          >
            {emptyTableText ?? ""}
          </Button>
        )
    );
  }
}
